import { PageBlock } from '@design-system/page-block';
import { Events, OobMode, OobToken } from '@phx-husky/auth-service';
import {
  AccessDeniedReason,
  AuthWrapper,
  AuthWrapperProps,
  SecureLevel,
} from '@phx-husky/auth-wrapper';
import { ForgotPassword } from '@phx-husky/forgot-password';
import { Login } from '@phx-husky/login';
import { MagicLinkHandler } from '@phx-husky/magic-link-handler';
import { Register } from '@phx-husky/register';
import { useLocation, WindowLocation } from '@reach/router';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import React, { forwardRef, ReactElement, useEffect, useState } from 'react';

import { useCurrentLang } from '~/hooks/useCurrentLang';
import { AuthQuery } from '~/layout/Auth/models';

import { Profile } from '../Profile';
import {
  FORGOT_PASSWORD_FORM,
  LOGIN_FORM,
  MAGIC_LINK_HANDLER,
  PAGE_ONLY_FOR_SIGNED_IN,
  PROFILE_FORM,
  REGISTER_FORM,
  URL_MODE_PARAM_NAME,
  URL_OOBCODE_PARAM_NAME,
  URL_SUCCESSFUL_SIGN_IN_PARAM_NAME,
  URL_SUCCESSFUL_SIGN_UP_PARAM_NAME,
} from './constants';
import { LoginRegisterBlocksProps, LocationState } from './models';
import { getMagicLinkHandlerSecureLevel, getMappedAuthLinks } from './utils';

const LoginRegisterBlocks = forwardRef<
  HTMLDivElement,
  LoginRegisterBlocksProps
  // eslint-disable-next-line max-statements
>(({ pageBlockUI, componentId, variant, uid, children }, ref) => {
  const location = useLocation() as WindowLocation<LocationState>;
  const { search: locationQueryString, state: locationState } = location;

  const {
    currentLang: { locale: currentLocaleCode },
  } = useCurrentLang();
  const {
    allContentstackAuthSettings: { nodes: authSettings },
  } = useStaticQuery<AuthQuery>(graphql`
    {
      allContentstackAuthSettings {
        nodes {
          authLinks: auth_links {
            ...AuthLinksFragment
          }
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
    }
  `);

  const {
    loginLinkHref,
    registerLinkHref,
    profileLinkHref,
    loginSuccessLinkHref,
    registerSuccessLinkHref,
    forgotPasswordLinkHref,
  } = getMappedAuthLinks(authSettings, currentLocaleCode);

  const [oobToken, setOobToken] = useState<OobToken | null>(null);
  const [successfulSignUp, setSuccessfulSignUp] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(locationQueryString || '');
    setSuccessfulSignUp(
      Boolean(queryParams.get(URL_SUCCESSFUL_SIGN_UP_PARAM_NAME))
    );

    const oobMode = queryParams.get(URL_MODE_PARAM_NAME) as OobMode | null;
    const oobCode = queryParams.get(URL_OOBCODE_PARAM_NAME);

    if (
      oobMode &&
      [
        OobMode.verifyEmail,
        OobMode.resetPassword,
        OobMode.verifyAndChangeEmail,
        OobMode.recoverEmail,
      ].includes(oobMode) &&
      oobCode
    ) {
      setOobToken({
        mode: oobMode,
        code: oobCode,
      });
    } else {
      if (variant === MAGIC_LINK_HANDLER) {
        navigate(loginLinkHref || '/');
      }

      setOobToken(null);
    }
  }, [locationQueryString]);

  const onInvalidAccess = (reason: AccessDeniedReason) => {
    let href: string | undefined;

    // eslint-disable-next-line default-case
    switch (reason) {
      case AccessDeniedReason.Logged:
        href = locationState?.from || profileLinkHref;
        break;
      case AccessDeniedReason.UserNotApproved:
      case AccessDeniedReason.NotVerified:
      case AccessDeniedReason.Unlogged:
        href = loginLinkHref;
        break;
      case AccessDeniedReason.UserRejected:
      case AccessDeniedReason.UserPending:
        return;
    }

    const queryParams = new URLSearchParams(locationQueryString || '');
    const normalizedHref = (href || '').endsWith('/') ? href : `${href}/`;

    navigate(normalizedHref, {
      replace: true,
      state: { from: location.pathname },
    });
  };

  const unloggedAuthWrapperProps: Omit<AuthWrapperProps, 'children'> = {
    secureLevel: SecureLevel.UnloggedOrNonVerified,
    onInvalidAccess,
  };

  const formTypeMap = {
    [LOGIN_FORM]: (
      <AuthWrapper {...unloggedAuthWrapperProps}>
        <Login
          key={locationState?.timestamp}
          onSuccess={() => {
            const from = location.state?.from;
            navigate(
              from ||
                `${
                  loginSuccessLinkHref || '/'
                }?${URL_SUCCESSFUL_SIGN_IN_PARAM_NAME}=true`
            );
          }}
          onNavigateToRegister={() => navigate(registerLinkHref || '/')}
          onForgotPassword={() => navigate(forgotPasswordLinkHref || '/')}
          onSuccessfulSignUp={() =>
            navigate(`${registerLinkHref}?successfulSignUp=true`)
          }
        />
      </AuthWrapper>
    ),

    [FORGOT_PASSWORD_FORM]: (
      <AuthWrapper {...unloggedAuthWrapperProps}>
        <ForgotPassword
          onClickSignIn={() => navigate(loginLinkHref || '/')}
          oobCode={
            oobToken?.mode === OobMode.resetPassword ? oobToken.code : undefined
          }
        />
      </AuthWrapper>
    ),

    [REGISTER_FORM]: (
      <AuthWrapper {...unloggedAuthWrapperProps}>
        <Register
          key={locationState?.timestamp}
          onLoginNavigate={() => navigate(loginLinkHref || '/')}
          onSuccessfulSignUp={() => navigate('?successfulSignUp=true')}
          successfulSignUp={successfulSignUp}
          onSignIn={() => navigate(profileLinkHref || '/')}
        />
      </AuthWrapper>
    ),

    [PROFILE_FORM]: (
      <AuthWrapper
        secureLevel={SecureLevel.Logged}
        onInvalidAccess={onInvalidAccess}
      >
        <Profile
          onAccountDelete={() =>
            window.dispatchEvent(new CustomEvent(Events.onDeleteAccount))
          }
        />
      </AuthWrapper>
    ),

    [MAGIC_LINK_HANDLER]: oobToken ? (
      <AuthWrapper
        secureLevel={getMagicLinkHandlerSecureLevel({
          oobMode: oobToken.mode,
        })}
        onInvalidAccess={onInvalidAccess}
      >
        <MagicLinkHandler
          oobCode={oobToken.code}
          oobMode={oobToken.mode}
          onLoginNavigate={() => navigate(loginLinkHref || '/')}
          onSuccessRegisterEmailVerification={() =>
            navigate(registerSuccessLinkHref || '/')
          }
          onSuccessEmailChange={() =>
            navigate(loginLinkHref || `/${LOGIN_FORM}`)
          }
        />
      </AuthWrapper>
    ) : null,
  };

  if (variant === PAGE_ONLY_FOR_SIGNED_IN) {
    return (
      <AuthWrapper
        secureLevel={SecureLevel.Logged}
        onInvalidAccess={onInvalidAccess}
      >
        {children}
      </AuthWrapper>
    );
  }

  return variant && formTypeMap[variant] ? (
    <PageBlock
      ref={ref}
      {...pageBlockUI}
      id={componentId}
      className="login-register-form"
      componentUid={uid}
    >
      {formTypeMap[variant]}
    </PageBlock>
  ) : null;
});

export default LoginRegisterBlocks;
